import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import PasswordStrengthMeter, { evaluatePassword } from './PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { current_password: '', new_password: '', confirm_password: '' };

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) { toast.error('New passwords do not match'); return; }
    if (!evaluatePassword(form.new_password).isStrong) { toast.error('Please choose a stronger password'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/portal/settings/password`, {
        current_password: form.current_password, new_password: form.new_password,
      }, { withCredentials: true });
      toast.success('Password updated');
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update password');
    } finally { setSaving(false); }
  };

  const field = (k, label, testid) => (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input type="password" required value={form[k]} onChange={set(k)} data-testid={testid}
        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]" />
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Enter your current password, then choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {field('current_password', 'Current password', 'change-password-current')}
          {field('new_password', 'New password', 'change-password-new')}
          <PasswordStrengthMeter password={form.new_password} testId="change-password-strength" />
          {field('confirm_password', 'Confirm new password', 'change-password-confirm')}
          <button type="submit" disabled={saving} data-testid="change-password-submit"
            className="w-full bg-[#1D4ED8] hover:bg-[#1E40AF] text-white font-semibold rounded-lg py-2.5 transition-colors disabled:opacity-60">
            {saving ? 'Saving...' : 'Update password'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
